/* Notification Center — shared Permit Library targets grid.
 *
 * One master/detail expandable grid used BOTH by the Rule Detail → Targets tab
 * and the New Rule wizard → Target step, so the two can never drift.  One main
 * row per parameter/analyte (Target/Analyte · Scope–Permit Type · Permit Name ·
 * Enabled toggle); expanding a row reveals that parameter's configured Permit
 * Library limits (Limit Type · Operator · Limit · Units · Effective From/To).
 *
 * INTERACTION CONTRACT (RTCFR requirement):
 *   • The Enabled state changes ONLY when the toggle control itself is clicked
 *     (its handler calls stopPropagation).  Clicking anywhere else on the row
 *     never flips Enabled.
 *   • A row click (and the chevron) only expands/collapses.
 *
 * An optional fuzzy search box filters by analyte, synonym, permit type, permit
 * name and the limit metadata — every token must match somewhere (so "drink
 * fluor" narrows to Fluoride under Drinking Water). */
import { Fragment, useState } from 'react';
import { fmtLocalDate } from '../../utils/datetime';
import { type PermitTarget, type PlLimitRow } from './permitLibrary';

/** The limit's value(s) as shown in the Limit column — for the "Between"
 *  operator both bounds, otherwise the single threshold (or qualitative word). */
export function limitValue(row: PlLimitRow): string {
  return row.operator === 'Between' && row.limit2 ? `${row.limit} – ${row.limit2}` : row.limit;
}

/** Lower-cased haystack for one target — everything a search should match. */
function haystack(t: PermitTarget): string {
  const lims = t.limits.map((l) => `${l.limitType} ${l.operator} ${l.limit} ${l.units}`).join(' ');
  return `${t.parameter} ${t.synonym ?? ''} ${t.scope} ${t.permitName} ${lims}`.toLowerCase();
}

/** Every whitespace token of the query must appear in the haystack (AND). */
function matches(t: PermitTarget, tokens: string[]): boolean {
  if (tokens.length === 0) return true;
  const hay = haystack(t);
  return tokens.every((tok) => hay.includes(tok));
}

export function PermitTargetsGrid({
  targets, enabled, onToggle, expanded, onExpand, showSearch = false,
  readOnly = false, emptyText = 'No parameters in the Permit Library.',
}: {
  targets: PermitTarget[];
  /** key → whether this parameter is monitored. Missing key = OFF (only an
   *  explicit `true` is ON, exactly what the Rule Detail Save persists). */
  enabled: Record<string, boolean>;
  /** Called ONLY from the toggle control (never from a row click). */
  onToggle: (key: string, analyte: string) => void;
  expanded: Record<string, boolean>;
  onExpand: (key: string) => void;
  showSearch?: boolean;
  /** View mode — the Enabled toggle is a static state indicator (not clickable);
   *  search is suppressed. Expand/collapse still works. */
  readOnly?: boolean;
  /** Message shown when there are no rows (e.g. "No targets enabled" in View). */
  emptyText?: string;
}) {
  const [q, setQ] = useState('');
  const tokens = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const rows = tokens.length ? targets.filter((t) => matches(t, tokens)) : targets;

  return (
    <>
      {showSearch && !readOnly && (
        <div className="nc-search" style={{ maxWidth: '100%', margin: '0 0 10px' }}>
          <i className="ti ti-search" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search parameters — analyte, permit type, permit name…"
            aria-label="Search target parameters"
          />
        </div>
      )}
      <table className="nc-tgt-table nc-md-table" style={{ border: 'none', borderRadius: 0 }}>
        <thead><tr>
          <th style={{ width: 34 }} aria-label="Expand" />
          <th>Target / Analyte</th>
          <th style={{ width: '20%' }}>Scope – Permit Type</th>
          <th style={{ width: '24%' }}>Permit Name</th>
          <th style={{ width: 90, textAlign: 'center' }}>Enabled</th>
        </tr></thead>
        <tbody>
          {rows.map((t) => {
            const open = !!expanded[t.key];
            // Explicit ON only — must match the Save, which persists only keys
            // set to true. A missing key rendering ON (the old `?? true`) showed
            // toggles ON that were then saved as OFF (0 target rows).
            const on = enabled[t.key] === true;
            return (
              <Fragment key={t.key}>
                <tr className={`nc-md-main${open ? ' open' : ''}`} onClick={() => onExpand(t.key)}>
                  <td style={{ textAlign: 'center' }}>
                    <button className="nc-expander" title={open ? 'Collapse' : 'Expand'} aria-expanded={open}
                      onClick={(e) => { e.stopPropagation(); onExpand(t.key); }}>
                      <i className={`ti ${open ? 'ti-chevron-down' : 'ti-chevron-right'}`} />
                    </button>
                  </td>
                  <td>
                    <div className="nc-tgt-name">{t.parameter}</div>
                    <div className="nc-tgt-scope">{t.synonym ? `${t.synonym} · ` : ''}{t.limits.length} limit{t.limits.length === 1 ? '' : 's'}</div>
                  </td>
                  <td><span className="nc-tgt-scope">{t.scope}</span></td>
                  <td><span className="nc-tgt-scope">{t.permitName}</span></td>
                  <td style={{ textAlign: 'center' }}>
                    {/* Enabled changes ONLY from the toggle — stopPropagation keeps the row click (expand)
                        from flipping it. In readOnly (View) mode it is a static state indicator. */}
                    {readOnly
                      ? <span className={`nc-toggle${on ? ' on' : ''}`} aria-hidden />
                      : <span className={`nc-toggle${on ? ' on' : ''}`} role="button" aria-pressed={on}
                          onClick={(e) => { e.stopPropagation(); onToggle(t.key, t.parameter); }} />}
                  </td>
                </tr>
                {open && (
                  <tr className="nc-md-detail">
                    <td />
                    <td colSpan={4}>
                      <div className="nc-sub-wrap">
                        <table className="nc-sub-table">
                          <thead><tr>
                            <th style={{ width: '20%' }}>Limit Type</th>
                            <th style={{ width: 90 }}>Operator</th>
                            <th style={{ width: '16%' }}>Limit</th>
                            <th style={{ width: '14%' }}>Units</th>
                            <th style={{ width: '18%' }}>Effective From</th>
                            <th style={{ width: '18%' }}>Effective To</th>
                          </tr></thead>
                          <tbody>
                            {t.limits.map((lim, li) => (
                              <tr key={li}>
                                <td><span className="nc-limit-type">{lim.limitType}</span></td>
                                <td><span className="nc-mono">{lim.operator}</span></td>
                                <td><span className="nc-mono">{limitValue(lim)}</span></td>
                                <td>{lim.units || '—'}</td>
                                <td>{fmtLocalDate(lim.effectiveFrom) || '—'}</td>
                                <td>{fmtLocalDate(lim.effectiveTo) || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {targets.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--nc-text-light)', padding: 22 }}>{emptyText}</td></tr>}
          {targets.length > 0 && rows.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--nc-text-light)', padding: 22 }}>No parameters match “{q.trim()}”.</td></tr>}
        </tbody>
      </table>
    </>
  );
}
